function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable no-underscore-dangle */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { uniqBy } from 'lodash';
import entityComposition from '../../common/classes/entityComposition';
import { labelSelector, valueSelector, enabledSelector, newPathSelector } from '../selectors';
import localChangeSelector from '../selectors/localChangeSelector';
import { UncomposedSinglePickerField as SinglePickerField } from './singlePickerField';
import OptionMultiPicker from './optionMultiPicker';
const writeMode = {
  REFERENCE: 'REFERENCE',
  DATA: 'DATA'
};
const toReference = actionValue => ({
  [actionValue.identifier]: {
    _formRef: actionValue.identifier
  }
});
const toData = actionValue => ({
  [actionValue.identifier]: {
    title: actionValue.title
  }
});
const toReferenceMulti = actionValue => {
  if (!actionValue) {
    return null;
  }
  const referenceValues = {};
  const actionValueKeys = Object.keys(actionValue);
  actionValueKeys.forEach(avKey => {
    referenceValues[actionValue[avKey].identifier || actionValue[avKey].id] = {
      _formRef: actionValue[avKey].identifier || actionValue[avKey].id
    };
  });
  return referenceValues;
};
const toDataMulti = actionValue => {
  if (!actionValue) {
    return null;
  }
  const dataValues = {};
  const actionValueKeys = Object.keys(actionValue);
  actionValueKeys.forEach(avKey => {
    dataValues[actionValue[avKey].identifier || actionValue[avKey].id || avKey] = {
      title: actionValue[avKey].title
    };
  });
  return dataValues;
};
const toIdentifiableValue = (mode, value, active = {}, allowsMultipleSelection = false) => {
  if (allowsMultipleSelection) {
    const keys = Object.keys(active);
    const activeValue = {};
    keys.forEach(key => {
      if (mode === writeMode.DATA && value) {
        if (!value[key]) {
          return null;
        }
        activeValue[key] = {
          identifier: key,
          title: value[key].title
        };
      } else {
        if (!active[key]) {
          return null;
        }
        activeValue[key] = _objectSpread(_objectSpread({}, active[key]), {}, {
          identifier: active[key].id
        });
      }
      return null;
    });
    return activeValue;
  }
  if (mode === writeMode.DATA && value) {
    const [key] = Object.keys(value);
    return {
      identifier: key,
      title: value[key].title
    };
  }
  if (!active.id) {
    return null;
  }
  return _objectSpread(_objectSpread({}, active), {}, {
    identifier: active.id
  });
};

/**
 * Look at the text filter that was applied and try to
 * identify the index that was matched during search.
 */
const toArrayOptions = (filters, options, activeValue, allowsMultipleSelection = false) => {
  const addSelectedValue = (opts = []) => {
    if (allowsMultipleSelection && activeValue) {
      const keys = Object.keys(activeValue);
      keys.forEach(key => {
        if (activeValue[key] && activeValue[key].identifier && !opts.some(opt => opt.identifier === activeValue[key].identifier)) {
          // If the shown option isn't in the list, put it there (at the first position)
          opts.unshift(activeValue[key]);
        }
      });
    } else if (activeValue && !opts.some(opt => opt.identifier === activeValue.identifier)) {
      // If the shown option isn't in the list, put it there (at the first position)
      opts.unshift(activeValue);
    }
    return opts;
  };
  const {
    text: textIndexes = []
  } = filters;
  const [firstIndex] = textIndexes;
  const validIndexes = textIndexes.map(({
    index
  }) => index);
  if (!firstIndex) {
    return addSelectedValue();
  }
  const getIndex = (opt = {}) => validIndexes.find(key => {
    const option = opt[key];
    // See if the text search matches the _value_ of the index
    return option === null || option === void 0 ? void 0 : option.toLowerCase().includes(firstIndex.value.toLowerCase());
  });
  const nonUniqueOpts = (options || []).map(option => {
    const index = getIndex(option);
    if (!index) {
      return null;
    }
    return _objectSpread(_objectSpread({}, option), {}, {
      title: option[index],
      identifier: option.id
    });
    // Filter out any options that don't match the current filter
    // (null case)
  }).filter(opt => !!opt);
  const opts = uniqBy(nonUniqueOpts, 'title');
  addSelectedValue(opts);
  return opts;
};
const FormSearchField = ({
  allowsMultipleSelection: _allowsMultipleSelection = false,
  allowsOther: _allowsOther = false,
  className: _className = '',
  contentPath: _contentPath = undefined,
  deferred,
  deferredActions,
  enabled,
  format,
  onChange,
  path,
  pickerType: _pickerType = undefined,
  value: _value = null,
  // contains { id: { _formRef } } in reference mode, display comes from active
  values: _values = null
}) => {
  const {
    serverShow,
    serverIndex
  } = deferredActions;
  const localChangeFunc = localChangeSelector({}, {
    allowsMultipleSelection: _allowsMultipleSelection,
    allowsOther: _allowsOther,
    contentPath: _contentPath,
    deferred,
    deferredActions,
    enabled,
    format,
    onChange,
    path,
    pickerType: _pickerType,
    value: _value,
    values: _values
  });
  const mode = _pickerType === 'formSearchField' ? writeMode.REFERENCE : writeMode.DATA;
  const toValue = mode === writeMode.REFERENCE ? toReference : toData;
  const toValues = mode === writeMode.REFERENCE ? toReferenceMulti : toDataMulti;
  let active;
  let loading;
  if (_allowsMultipleSelection && _value && Object.keys(_value).length > 0) {
    const valueKeys = Object.keys(_value);
    const newActive = {};
    valueKeys.forEach(key => {
      var _deferred, _deferred$_value$key$;
      newActive[_value[key]._formRef || key] = ((_deferred = deferred[_value[key]._formRef || key]) === null || _deferred === void 0 ? void 0 : _deferred.active) || _value[key];
      loading = ((_deferred$_value$key$ = deferred[_value[key]._formRef]) === null || _deferred$_value$key$ === void 0 ? void 0 : _deferred$_value$key$.loading) || false;
    });
    active = newActive;
  } else if (!_allowsMultipleSelection) {
    var _deferred$id, _deferred$id2;
    let id = _value === null || _value === void 0 ? void 0 : _value.id;
    if (_value && Object.keys(_value).length > 0) {
      id = _value[Object.keys(_value)[0]]._formRef;
    }
    active = (deferred === null || deferred === void 0 || (_deferred$id = deferred[id]) === null || _deferred$id === void 0 ? void 0 : _deferred$id.active) || {};
    loading = (deferred === null || deferred === void 0 || (_deferred$id2 = deferred[id]) === null || _deferred$id2 === void 0 ? void 0 : _deferred$id2.loading) || false;
  }
  const {
    // deferred data uses path entries so each formSearchField can have its own
    // search and index state
    [path]: {
      indexQuery,
      indexQuery: {
        filters = {}
      } = {},
      hasNext = false,
      options = []
    } = {}
  } = deferred;
  const displayValue = actionValue => {
    if (!actionValue) {
      return null;
    }
    if (_allowsMultipleSelection) {
      return Object.values(actionValue || {}).map(av => av.title).join(', ');
    }
    return actionValue === null || actionValue === void 0 ? void 0 : actionValue.title;
  };
  const wrappedOnChange = action => {
    const {
      value: actionValue
    } = action;
    if (!actionValue) {
      return onChange(localChangeFunc(_objectSpread(_objectSpread({}, action), {}, {
        path,
        displayValue: displayValue(actionValue)
      })));
    }
    return onChange(localChangeFunc(_objectSpread(_objectSpread({}, action), {}, {
      value: _allowsMultipleSelection ? toValues(actionValue) : toValue(actionValue),
      path,
      displayValue: displayValue(actionValue)
    })));
  };
  useEffect(() => {
    if (mode === writeMode.DATA) {
      return;
    }

    // value has been cleared
    if (!_value) {
      return;
    }
    if (_allowsMultipleSelection) {
      const keys = Object.keys(_value); // value is in the form of { id: { _formRef: id } }
      keys.forEach(key => {
        var _active$key;
        // Avoid reloading the same record.
        // However, when lots of formSearchFields are on the same page
        // loading appears to be done simultaneously, so this check doesn't always work and an
        // additional check is necessary in serverShow
        if (key && active && !((_active$key = active[key]) !== null && _active$key !== void 0 && _active$key.title) && !loading) {
          serverShow(key, {
            // was used in the reducer to set the active value keyed by path, but now just uses id
            path,
            contentPaths: ['title'] // used by the server to limit the fields returned
          });
        }
      });
    } else {
      const [key] = Object.keys(_value); // value is in the form of { id: { _formRef: id } }

      // Avoid reloading the same record.
      // However, when lots of formSearchFields are on the same page
      // loading appears to be done simultaneously, so this check doesn't always work and an
      // additional check is necessary in serverShow
      // Call when we have a value ref but haven't loaded the record yet
      // (active.title === undefined).
      // Once loaded, active.title is set (string or null); do not call again to avoid reloading.
      if (key && active.title === undefined && !loading) {
        serverShow(key, {
          // was used in the reducer to set the active value keyed by path, but now just uses id
          path,
          contentPaths: ['title'] // used by the server to limit the fields returned
        });
      }
    }
  }, [loading, mode, path, serverShow, _value, active, _allowsMultipleSelection]);
  const onTextChange = text => {
    const {
      formTypeIdentifiers,
      indexes = ['title']
    } = format;
    const filterIndexes = indexes.map(index => ({
      index,
      value: text
    }));
    if (!text || text.length === 0) {
      return;
    }
    serverIndex(formTypeIdentifiers, path, 'deferred', _objectSpread(_objectSpread({}, indexQuery), {}, {
      filters: _objectSpread({
        text: filterIndexes
      }, format.filters)
    }));
  };
  const activeValue = toIdentifiableValue(mode, _value, active, _allowsMultipleSelection);
  if (_allowsMultipleSelection) {
    return /*#__PURE__*/React.createElement(OptionMultiPicker, {
      allowsMultipleSelection: _allowsMultipleSelection,
      allowsOther: _allowsOther,
      allowCreate: _allowsOther,
      className: _className,
      onChange: wrappedOnChange,
      options: toArrayOptions(filters, options, activeValue, _allowsMultipleSelection),
      value: activeValue,
      values: _values,
      onTextChange: onTextChange,
      dropdownIndicatorGlyph: "magnifying-glass",
      placeholder: "Start typing to search...",
      endMessage: hasNext ? 'There are more results, continue to type to filter...' : null,
      isLoading: loading || false,
      isSearching: loading || false,
      enabled: enabled,
      path: path,
      labelKey: "title",
      valueKey: "identifier",
      verticalPadding: false
    });
  }
  return /*#__PURE__*/React.createElement(SinglePickerField, {
    format: format,
    allowsOther: _allowsOther,
    className: _className,
    onChange: wrappedOnChange,
    options: toArrayOptions(filters, options, activeValue, _allowsMultipleSelection),
    value: activeValue,
    onTextChange: onTextChange,
    dropdownIndicatorGlyph: "magnifying-glass",
    placeholder: "Start typing to search...",
    endMessage: hasNext ? 'There are more results, continue to type to filter...' : null,
    isLoading: loading || false,
    isSearching: loading || false,
    enabled: enabled,
    path: path
  });
};
FormSearchField.propTypes = {
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  contentPath: PropTypes.string,
  format: PropTypes.shape({
    label: PropTypes.string,
    indexes: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.shape({}),
    formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  deferred: PropTypes.shape({}).isRequired,
  deferredActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverShow: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  indexes: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  values: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.string)]),
  pickerType: PropTypes.oneOf(['formSearchField', 'optionSearchField']),
  path: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired
};
export { FormSearchField as UncomposedFormSearchField };
export default entityComposition(FormSearchField, ['deferred'], {
  enabled: enabledSelector,
  label: labelSelector,
  path: newPathSelector,
  value: valueSelector
});